import { Avatar, Box, ButtonBase, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";
import { useHistory } from "react-router";

import { Agent } from "@/api/generated";
import { AgentBeginnerMessage } from "@/components/agent/AgentBeginnerMessage";
import { BottomSheet } from "@/components/BottomSheet";
import { CustomButton } from "@/components/Button";
import { Chevron } from "@/components/Chevron";
import { Loading } from "@/components/Loading";
import { layoutTokens } from "@/designTokens";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import theme from "@/theme";
import { AGENT_DETAIL_URL_SOURCE } from "@/utilities/userOffer";

import NoImage from "@assets/image/NoImage.svg";

const useStyles = makeStyles(() => ({
  cards: { display: "flex", flexDirection: "column", gap: theme.spacing(2), padding: theme.spacing(2, 0) },
  card: {
    width: "100%",
    padding: theme.spacing(2),
    textAlign: "left",
    gap: theme.spacing(2),
    justifyContent: "flex-start",
    flexDirection: "column",
    alignItems: "stretch",
    borderRadius: layoutTokens.radius.lg,
    border: `1px solid ${theme.palette.semantic["border/default"]}`,
  },
  header: { display: "flex", alignItems: "center", gap: theme.spacing(1.5) },
  comment: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.semantic["border/default"]}`,
  },
  commentLabel: {
    alignSelf: "flex-start",
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.semantic["action/primary"],
    backgroundColor: theme.palette.semantic["state/selected-bg"],
    borderRadius: layoutTokens.radius.sm,
    padding: theme.spacing(0.5, 1),
  },
  commentBody: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    fontSize: 14,
    lineHeight: 1.5,
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: { fontSize: 16 },
  },
  avatar: { width: 64, height: 64, flexShrink: 0 },
  name: { fontSize: 16, fontWeight: 700, lineHeight: 1.3, overflowWrap: "anywhere" },
  company: { fontSize: 12, color: theme.palette.text.secondary, marginTop: theme.spacing(1), overflowWrap: "anywhere" },
  footer: { display: "flex", flexDirection: "column", gap: theme.spacing(1) },
}));
export const ArticleScrollModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const classes = useStyles();
  const history = useHistory();
  const [agents, setAgents] = React.useState<Agent[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);
  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    PropallyApiFactory()
      .v1AgentsFeedsSectionPost("active", 1, 5, undefined, undefined, { withCredentials: true })
      .then((res) => {
        if (active) setAgents((res.data as { agents?: Agent[] }).agents ?? []);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [attempt]);
  return (
    <BottomSheet
      open
      onOpen={() => {}}
      onClose={onClose}
      title="この章の内容を相談できます"
      description="利回りの見方や物件の選び方は、プランナーに直接聞けます"
      footer={
        <Box className={classes.footer}>
          <CustomButton
            tone="outline"
            buttonSize="M"
            onClick={() => history.push(`/agent_search?source=${AGENT_DETAIL_URL_SOURCE.LEARNING_ARTICLE}`)}
          >
            他の認定プランナーも見てみる
          </CustomButton>
          <CustomButton tone="text" buttonSize="L" onClick={onClose}>
            あとで
          </CustomButton>
        </Box>
      }
    >
      <Box className={classes.cards}>
        {loading ? (
          <Loading />
        ) : error ? (
          <Box role="alert">
            <Typography>プランナーの取得に失敗しました。</Typography>
            <CustomButton tone="outline" buttonSize="M" onClick={() => setAttempt((value) => value + 1)}>
              再読み込み
            </CustomButton>
          </Box>
        ) : agents.length === 0 ? (
          <Typography>現在ご案内できるプランナーがいません。</Typography>
        ) : (
          agents.map((agent) => (
            <ButtonBase
              key={agent.id}
              className={classes.card}
              disabled={!Number.isInteger(agent.id) || Number(agent.id) <= 0}
              onClick={() =>
                history.push(`/agent_search/${agent.id}?source=${AGENT_DETAIL_URL_SOURCE.LEARNING_ARTICLE}`)
              }
            >
              <Box className={classes.header}>
                <Avatar className={classes.avatar} src={agent.agent_profile_image?.image_url || NoImage} alt="" />
                <Box flex={1} minWidth={0}>
                  <Typography className={classes.name}>
                    {agent.family_name} {agent.first_name}
                  </Typography>
                  <AgentBeginnerMessage message={agent.beginner_message} variant="card" />
                </Box>
                {!agent.propally_comment?.comment && <Chevron />}
              </Box>
              {agent.propally_comment?.comment && (
                <Box className={classes.comment}>
                  <Typography className={classes.commentLabel}>プロパリーからのコメント</Typography>
                  <Box className={classes.commentBody}>
                    <Box flex={1}>{agent.propally_comment.comment}</Box>
                    <Chevron />
                  </Box>
                </Box>
              )}
              <Typography className={classes.company}>{agent.company?.name}</Typography>
            </ButtonBase>
          ))
        )}
      </Box>
    </BottomSheet>
  );
};
